import React from 'react';
import { createPortal } from 'react-dom';
import { SoldierAvatar as BaseAvatar } from '@/components/ui/admin/check-in';
import { Icon } from '@/components/ui/icon';
import { soldierRoleOptions } from '@/db/schema/soldiers';
import { ageFrom } from '@/lib/age';
import { cn } from '@/lib/utils';
import {
  type BataillonDetail,
  type BataillonMember,
  type BataillonOverview,
  type BataillonTask,
  type CommandPerson,
  TASK_PHASE_LABEL,
  TASK_PHASE_ORDER,
  type TaskPhase,
} from '@/services/bataillons';

/**
 * Batalhões — prints 01–06 de `export-batalhoes/admin/prints`.
 * Tudo aqui segue as leis do campo e a paleta do design system do admin. Nenhum hex fora do dossiê
 * (que é papel branco e usa cores de impressão próprias, ver `DossierSheet`).
 */

/** Servida pelo `/assets/*` do servidor (public/military-bg.jpg). */
const COVER_URL = '/assets/military-bg.jpg';

export const pl = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;
/** O client do Eden converte "2010-01-01" em `Date` (UTC); `ageFrom` quer o texto `YYYY-MM-DD`. */
export const ageOf = (birthday: string | Date) =>
  ageFrom(birthday instanceof Date ? birthday.toISOString().slice(0, 10) : birthday) ?? 0;
export const memberMeta = (m: BataillonMember) =>
  [
    m.role ? soldierRoleOptions.find((o) => o.value === m.role)?.label : null,
    m.soldierNumber ? `Nº ${m.soldierNumber}` : null,
    `${ageOf(m.birthday)} anos`,
    m.city,
  ]
    .filter(Boolean)
    .join(' • ');

/** Efetivo separado por público: recruta de evento Kids → Kids; Forja/Outro → Jovens. Grupos vazios saem. */
export const memberGroups = (members: BataillonMember[]) =>
  [
    { key: 'kids', label: 'Kids', items: members.filter((m) => m.kind === 'kids') },
    { key: 'youth', label: 'Jovens', items: members.filter((m) => m.kind !== 'kids') },
  ].filter((g) => g.items.length);

/* ───────────── Peças locais da tela (não mexem nos componentes compartilhados do admin) ───────────── */

const initialsOf = (n: string) =>
  n
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

/** Avatar com tamanhos compactos (xs 24 · sm 28) para selects e listas; `md` usa o `SoldierAvatar` do admin. */
export const SoldierAvatar: React.FC<{
  name: string;
  photo?: string | null;
  size?: 'xs' | 'sm' | 'md';
}> = ({ name, photo, size = 'md' }) => {
  const [broken, setBroken] = React.useState(false);
  if (size === 'md') return <BaseAvatar name={name} photo={photo} />;
  const cls = cn(
    'shrink-0 rounded-full border border-border',
    size === 'xs' ? 'size-6' : 'size-7',
  );
  return photo && !broken ? (
    <img
      src={photo}
      alt=""
      onError={() => setBroken(true)}
      className={cn(cls, 'object-cover')}
    />
  ) : (
    <span
      className={cn(
        cls,
        'font-display flex items-center justify-center bg-sunken text-[9.5px] font-extrabold text-muted-foreground',
      )}
    >
      {initialsOf(name)}
    </span>
  );
};

/** Card de seção numerada (igual ao `SectionCard` do admin) com `trailing` ao lado do título e `flush` sem padding no corpo (e com cantos cortando o conteúdo). Sem `flush` o overflow fica visível para o popover do select de comando. */
export const BattalionSection: React.FC<{
  index: string;
  title: string;
  description: string;
  trailing?: React.ReactNode;
  flush?: boolean;
  children: React.ReactNode;
}> = ({ index, title, description, trailing, flush, children }) => (
  <section
    className={cn('rounded-2xl border border-border bg-card', flush && 'overflow-hidden')}
  >
    <div className="flex items-start gap-4 border-b border-border px-[26px] py-[22px]">
      <span className="font-display pt-[3px] text-[13px] font-extrabold text-faint">
        {index}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="font-display text-[18px] font-extrabold uppercase tracking-tight text-white">
          {title}
        </span>
        <span className="text-[13px] text-muted-foreground">{description}</span>
      </span>
      {trailing}
    </div>
    <div className={cn('flex flex-col', !flush && 'gap-4 p-[26px]')}>{children}</div>
  </section>
);

/** Barra de salvar fixa (mesma geometria da `SaveBar` do admin) com rótulos "Voltar" / "Salvar batalhão". */
export const BattalionSaveBar: React.FC<{
  status: 'clean' | 'dirty' | 'saved';
  onBack: () => void;
  onSave: () => void;
  saving?: boolean;
}> = ({ status, onBack, onSave, saving }) => (
  <div className="fixed inset-x-0 bottom-0 z-25 border-t border-border bg-background/90 px-[clamp(20px,2.4vw,32px)] py-3.5 backdrop-blur-[20px] md:left-(--sidebar-width)">
    <div className="flex flex-wrap items-center gap-2.5">
      <span
        className={cn(
          'mr-auto flex items-center gap-2.5 text-[12.5px]',
          status === 'dirty' ? 'text-sand' : 'text-muted-foreground',
        )}
      >
        <span
          className={cn(
            'size-[7px] rounded-full',
            status === 'dirty' ? 'bg-sand' : 'bg-olive-icon',
          )}
        />
        {
          {
            clean: 'Tudo atualizado',
            dirty: 'Alterações não salvas',
            saved: 'Alterações salvas',
          }[status]
        }
      </span>
      <button
        type="button"
        onClick={onBack}
        className="flex h-11 items-center rounded-full border border-border px-[22px] text-[13.5px] font-semibold text-muted-foreground transition-colors hover:text-white"
      >
        Voltar
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={status !== 'dirty' || saving}
        className="flex h-11 items-center gap-2 rounded-full bg-sand px-6 text-[13.5px] font-bold text-on-sand transition-colors hover:bg-sand/90 disabled:opacity-40"
      >
        <Icon name="save" className="size-4" />
        Salvar batalhão
      </button>
    </div>
  </div>
);

/* ───────────── Selo / checkbox ───────────── */

/** Selo da sigla: 40px na lista, 88px na ficha (areia), raio 11/20. */
export const BattalionTag: React.FC<{ tag: string | null; size?: 'md' | 'xl' }> = ({
  tag,
  size = 'md',
}) => (
  <span
    className={cn(
      'font-display flex shrink-0 items-center justify-center font-extrabold tracking-[.04em]',
      size === 'xl'
        ? 'size-[88px] rounded-[20px] border border-olive-line bg-accent/85 text-[24px] text-sand'
        : 'size-10 rounded-[11px] border border-border bg-sunken text-[11px] text-olive-ink',
    )}
  >
    {tag ?? '—'}
  </span>
);

/** Checkbox quadrado 20px: marcado = areia cheia com check preto. */
export const SquareCheck: React.FC<{
  checked: boolean;
  onChange?: () => void;
  label?: string;
  /** Só desenha a caixa (a linha pai, um <button>, é quem recebe o clique) — evita button dentro de button. */
  decorative?: boolean;
}> = ({ checked, onChange, label, decorative }) => {
  const cls = cn(
    'flex size-5 shrink-0 items-center justify-center rounded-md border text-on-sand transition-colors',
    checked ? 'border-sand bg-sand' : 'border-ring',
  );
  const mark = checked && <Icon name="check" className="size-3" strokeWidth={2.6} />;
  if (decorative)
    return (
      <span aria-hidden className={cls}>
        {mark}
      </span>
    );
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onChange?.();
      }}
      className={cls}
    >
      {mark}
    </button>
  );
};

/* ───────────── Listagem (print 01/02) ───────────── */

export const BATTALION_COLUMNS =
  '40px minmax(200px,1.4fr) repeat(4,minmax(112px,1fr)) 80px 140px 84px';

const CommandCell: React.FC<{ person: CommandPerson | null; withAvatar?: boolean }> = ({
  person,
  withAvatar,
}) =>
  person ? (
    <span className="flex min-w-0 items-center gap-[9px]">
      {withAvatar && (
        <SoldierAvatar
          name={person.name}
          photo={person.image ?? person.photo}
          size="sm"
        />
      )}
      <span className="truncate text-[13px] text-foreground">{person.name}</span>
    </span>
  ) : (
    <span className="text-[13px] text-sand-dim">Não definido</span>
  );

export const BattalionRow: React.FC<{
  b: BataillonOverview;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onPrint: () => void;
}> = ({ b, selected, onToggle, onOpen, onPrint }) => (
  <div
    className={cn(
      'grid items-center border-b border-border px-[22px] transition-colors hover:bg-raised',
      selected && 'bg-sand/[.04]',
    )}
    style={{ gridTemplateColumns: BATTALION_COLUMNS }}
  >
    <SquareCheck checked={selected} onChange={onToggle} label={`Selecionar ${b.name}`} />
    <button
      type="button"
      onClick={onOpen}
      className="flex min-w-0 items-center gap-3 py-3.5 text-left"
    >
      <BattalionTag tag={b.tag} />
      <span className="flex min-w-0 flex-col gap-[3px]">
        <span className="truncate text-[14px] font-semibold text-white">{b.name}</span>
        <span className="text-[11.5px] text-faint">
          {(b.isActive ? 'Ativo' : 'Inativo') +
            ' • ' +
            pl(b.tasksTotal, 'missão', 'missões')}
        </span>
      </span>
    </button>
    <CommandCell person={b.kidsMajor} withAvatar />
    <CommandCell person={b.kidsCaptain} />
    <CommandCell person={b.major} withAvatar />
    <CommandCell person={b.youthCaptain} />
    <span className="font-display text-right text-[16px] font-extrabold text-white">
      {b.soldiersCount}
    </span>
    <span className="flex flex-col gap-1.5 pl-6">
      <span className="text-[11.5px] font-semibold text-muted-foreground">{`${b.tasksDone}/${b.tasksTotal} concluídas`}</span>
      <MiniBar value={b.tasksTotal ? b.tasksDone / b.tasksTotal : 0} />
    </span>
    <span className="flex justify-end gap-[7px]">
      <RowIconButton icon="file-text" label="Emitir dossiê" onClick={onPrint} />
      <RowIconButton icon="pencil" label="Abrir ficha" tone="accent" onClick={onOpen} />
    </span>
  </div>
);

export const MiniBar: React.FC<{ value: number; className?: string }> = ({
  value,
  className,
}) => (
  <span className={cn('h-1 rounded-full bg-sand/[.07]', className)}>
    <span
      className="block h-full rounded-full bg-olive-icon transition-[width] duration-200"
      style={{ width: `${Math.round(value * 100)}%` }}
    />
  </span>
);

const RowIconButton: React.FC<{
  icon: React.ComponentProps<typeof Icon>['name'];
  label: string;
  tone?: 'neutral' | 'accent' | 'danger';
  onClick: () => void;
}> = ({ icon, label, tone = 'neutral', onClick }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={cn(
      'flex size-8 items-center justify-center rounded-[9px] border border-border transition-colors hover:border-ring hover:bg-raised',
      tone === 'accent'
        ? 'text-olive-icon'
        : tone === 'danger'
          ? 'text-destructive'
          : 'text-muted-foreground',
    )}
  >
    <Icon name={icon} className="size-[15px]" />
  </button>
);

/**
 * Barra flutuante da seleção (print 02). `sticky bottom-6` dentro do conteúdo da listagem
 * (não `fixed`) — acompanha a largura da área de conteúdo, sem calcular o sidebar.
 */
export const BulkDossierBar: React.FC<{
  count: number;
  onClear: () => void;
  onEmit: () => void;
  loading?: boolean;
}> = ({ count, onClear, onEmit, loading }) => (
  <div className="pointer-events-none sticky bottom-6 z-25 -mt-[76px] flex justify-center">
    <div className="anim-ci-in pointer-events-auto flex items-center gap-3.5 rounded-full border border-ring bg-card py-2.5 pl-5 pr-2.5 shadow-[0_10px_30px_rgba(0,0,0,.7)]">
      <span className="font-display flex h-7 min-w-7 items-center justify-center rounded-full bg-sand px-[9px] text-[13px] font-extrabold text-on-sand">
        {count}
      </span>
      <span className="text-[13.5px] font-semibold text-white">
        {count === 1 ? 'batalhão selecionado' : 'batalhões selecionados'}
      </span>
      <button
        type="button"
        onClick={onClear}
        className="col-label px-1.5 text-[10.5px] text-muted-foreground hover:text-white"
      >
        Limpar
      </button>
      <button
        type="button"
        onClick={onEmit}
        disabled={loading}
        className="flex h-11 items-center gap-2 rounded-full bg-sand px-[22px] text-[13.5px] font-bold text-on-sand hover:bg-sand/90 disabled:opacity-40"
      >
        <Icon name="file-text" className="size-4" />
        {loading ? 'Montando dossiê…' : 'Emitir dossiê dos selecionados'}
      </button>
    </div>
  </div>
);

/* ───────────── Ficha (print 03) ───────────── */

export const BattalionHero: React.FC<{
  b: BataillonDetail;
  soldiers: number;
  tasksLabel: string;
  commandFilled: number;
}> = ({ b, soldiers, tasksLabel, commandFilled }) => (
  <div
    className="relative overflow-hidden rounded-[18px] border border-border bg-cover bg-[center_42%]"
    style={{ backgroundImage: `url(${COVER_URL})` }}
  >
    <span className="absolute inset-0 bg-[linear-gradient(90deg,rgba(9,11,6,.96)_0%,rgba(9,11,6,.88)_55%,rgba(9,11,6,.6)_100%)]" />
    <div className="relative flex flex-wrap items-end gap-x-8 gap-y-[22px] px-[clamp(22px,2.4vw,32px)] py-[26px]">
      <BattalionTag tag={b.tag} size="xl" />
      <span className="flex min-w-0 flex-[1_1_300px] flex-col gap-3">
        <span className="flex flex-wrap items-center gap-2">
          <span className="col-label flex items-center gap-2 text-[10px] tracking-[.18em] text-sand-dim">
            <span className="h-px w-[18px] bg-sand-dim" />
            Ficha do batalhão
          </span>
          <ActivePill active={b.isActive} />
        </span>
        <span className="font-display text-[clamp(28px,3vw,42px)] font-extrabold uppercase leading-[.95] tracking-[-.035em] text-white">
          {b.name}
        </span>
      </span>
      <div className="grid grid-cols-3 rounded-[14px] border border-border bg-sunken/75">
        <HeroStat label="Soldados" value={String(soldiers)} />
        <HeroStat label="Missões" value={tasksLabel} tone="olive" />
        <HeroStat
          label="Comando"
          value={`${commandFilled}/4`}
          tone={commandFilled === 4 ? 'olive' : 'sand'}
        />
      </div>
    </div>
  </div>
);

const HeroStat: React.FC<{
  label: string;
  value: string;
  tone?: 'white' | 'olive' | 'sand';
}> = ({ label, value, tone = 'white' }) => (
  <span className="flex flex-col gap-1.5 border-l border-border px-[22px] py-3.5 first:border-l-0">
    <span className="col-label text-faint">{label}</span>
    <span
      className={cn(
        'font-display text-[22px] font-extrabold leading-none',
        tone === 'olive'
          ? 'text-olive-ink'
          : tone === 'sand'
            ? 'text-sand'
            : 'text-white',
      )}
    >
      {value}
    </span>
  </span>
);

export const ActivePill: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    className={cn(
      'col-label flex h-6 items-center gap-[7px] rounded-full border bg-accent/80 px-2.5 text-[10px]',
      active
        ? 'border-olive-line/40 text-olive-ink'
        : 'border-border text-muted-foreground',
    )}
  >
    <span
      className={cn(
        'size-1.5 rounded-full',
        active ? 'bg-olive-icon' : 'bg-muted-foreground',
      )}
    />
    {active ? 'Ativo' : 'Inativo'}
  </span>
);

/** Interruptor "Situação" ao lado do Nome — mesma caixa do Field (raio 14, bg-field). */
export const StatusSwitchField: React.FC<{
  value: boolean;
  onChange: (v: boolean) => void;
}> = ({ value, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={value}
    onClick={() => onChange(!value)}
    className="flex items-center gap-3 rounded-[14px] border border-border bg-field px-[18px]"
  >
    <span className="flex flex-col gap-1.5 text-left">
      <span className="col-label text-muted-foreground">Situação</span>
      <span
        className={cn(
          'text-[14px] font-semibold',
          value ? 'text-olive-ink' : 'text-muted-foreground',
        )}
      >
        {value ? 'Ativo' : 'Inativo'}
      </span>
    </span>
    <span
      className={cn(
        'relative h-6 w-[42px] rounded-full border transition-colors',
        value ? 'border-olive-line bg-olive' : 'border-border',
      )}
    >
      <span
        className={cn(
          'absolute top-[3px] size-4 rounded-full transition-[left]',
          value ? 'left-[22px] bg-white' : 'left-[3px] bg-faint',
        )}
      />
    </span>
  </button>
);

/**
 * Select de comando (Major Kids / Capitão Kids / Major Jovens / Capitão Jovens). Caixa com ícone 36px + rótulo DENTRO
 * ("MAJOR RESPONSÁVEL • USUÁRIOS") + valor 15.5px com avatar 24px; aberto = popover com busca.
 * Fecha com Esc e clique fora. `options` vem do hook certo (majors → Usuários, captains → Recrutas).
 */
export const CommandSelect: React.FC<{
  label: string;
  source: 'Usuários' | 'Recrutas';
  icon: React.ComponentProps<typeof Icon>['name'];
  value: CommandPerson | null;
  options: (CommandPerson & { meta?: string })[];
  search: string;
  onSearch: (v: string) => void;
  onPick: (p: CommandPerson | null) => void;
  loading?: boolean;
}> = ({ label, source, icon, value, options, search, onSearch, onPick, loading }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (
        e instanceof KeyboardEvent
          ? e.key === 'Escape'
          : !ref.current?.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          onSearch('');
        }}
        className={cn(
          'flex w-full items-center gap-3.5 rounded-[14px] border py-3 pl-3.5 pr-4 text-left transition-colors',
          open ? 'border-olive-line bg-accent' : 'border-border bg-field',
        )}
      >
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-[10px] border border-border bg-sunken',
            value ? 'text-olive-icon' : 'text-faint',
          )}
        >
          <Icon name={icon} className="size-[17px]" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-[5px]">
          <span className="col-label text-muted-foreground">
            {label} <span className="tracking-[.1em] text-faint">• {source}</span>
          </span>
          <span className="flex min-w-0 items-center gap-[9px]">
            {value && (
              <SoldierAvatar
                name={value.name}
                photo={value.image ?? value.photo}
                size="xs"
              />
            )}
            <span
              className={cn(
                'truncate text-[15.5px] font-semibold',
                value ? 'text-white' : 'text-faint',
              )}
            >
              {value?.name ?? 'Selecionar...'}
            </span>
          </span>
        </span>
        <Icon name="chevron-down" className="size-[17px] shrink-0 text-faint" />
      </button>
      {open && (
        <div className="anim-ci-in absolute inset-x-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-[14px] border border-ring bg-card shadow-[0_10px_30px_rgba(0,0,0,.7)]">
          <div className="flex h-12 items-center gap-2.5 border-b border-border px-4">
            <Icon name="search" className="size-4 text-olive-icon" />
            <input
              // biome-ignore lint/a11y/noAutofocus: popover/dialog recém-aberto, foco direto na busca (spec da tela)
              autoFocus
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={
                source === 'Usuários' ? 'Buscar usuário...' : 'Buscar recruta...'
              }
              className="min-w-0 flex-1 bg-transparent text-[14px] text-white outline-none placeholder:text-faint"
            />
          </div>
          <div className="max-h-[252px] overflow-y-auto">
            {value && (
              <button
                type="button"
                onClick={() => {
                  onPick(null);
                  setOpen(false);
                }}
                className="col-label w-full border-b border-border px-4 py-2.5 text-left text-muted-foreground hover:bg-raised"
              >
                Remover seleção
              </button>
            )}
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  onPick(o);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-raised',
                  o.id === value?.id && 'bg-accent',
                )}
              >
                <SoldierAvatar name={o.name} photo={o.image ?? o.photo} size="sm" />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-[13.5px] font-semibold text-white">{o.name}</span>
                  {o.meta && <span className="text-[11.5px] text-faint">{o.meta}</span>}
                </span>
                {o.id === value?.id && (
                  <Icon
                    name="check"
                    className="size-4 text-olive-icon"
                    strokeWidth={2.2}
                  />
                )}
              </button>
            ))}
            {!loading && !options.length && (
              <p className="px-4 py-6 text-center text-[13px] text-muted-foreground">
                Nenhum resultado.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ───────────── Missões (seção 03) ───────────── */

export const TaskComposer: React.FC<{
  onAdd: (title: string, phase: TaskPhase) => void;
}> = ({ onAdd }) => {
  const [v, setV] = React.useState('');
  const [phase, setPhase] = React.useState<TaskPhase>('before');
  const submit = () => {
    const t = v.trim();
    if (t) {
      onAdd(t, phase);
      setV('');
    }
  };
  return (
    <div className="flex flex-col gap-3 border-b border-border px-[26px] py-4">
      <div className="flex h-[52px] items-center gap-3 rounded-[14px] border border-border bg-field pl-4 pr-2 transition-colors focus-within:border-olive-line focus-within:bg-accent">
        <Icon name="plus" className="size-[17px] text-olive-icon" />
        <input
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Nova missão — Ex: Montar a tenda do refeitório"
          className="min-w-0 flex-1 bg-transparent text-[14.5px] font-medium text-white outline-none placeholder:text-faint"
        />
        <button
          type="button"
          onClick={submit}
          className={cn(
            'h-[38px] rounded-[10px] px-4 text-[12.5px] font-bold',
            v.trim() ? 'bg-sand text-on-sand' : 'bg-raised text-faint',
          )}
        >
          Adicionar
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="col-label mr-1 text-faint">Quando</span>
        {TASK_PHASE_ORDER.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPhase(p)}
            className={cn(
              'flex h-[30px] items-center rounded-full border px-3 text-[12px] font-bold transition-colors',
              phase === p
                ? 'border-white bg-white text-black'
                : 'border-border text-muted-foreground hover:text-white',
            )}
          >
            {TASK_PHASE_LABEL[p]}
          </button>
        ))}
      </div>
    </div>
  );
};

/** Filtro da lista de missões por fase (Todas | Antes | No retiro | Pós-retiro), com contagem. */
export const TaskPhaseFilter: React.FC<{
  value: TaskPhase | 'all';
  onChange: (v: TaskPhase | 'all') => void;
  counts: Record<TaskPhase | 'all', number>;
}> = ({ value, onChange, counts }) => (
  <div className="flex flex-wrap items-center gap-2 border-b border-border px-[26px] py-3">
    <span className="col-label mr-1 text-faint">Mostrar</span>
    {(['all', ...TASK_PHASE_ORDER] as const).map((p) => (
      <button
        key={p}
        type="button"
        onClick={() => onChange(p)}
        className={cn(
          'flex h-[28px] items-center gap-1.5 rounded-full border px-3 text-[12px] font-bold transition-colors',
          value === p
            ? 'border-sand bg-sand text-on-sand'
            : 'border-border text-muted-foreground hover:text-white',
        )}
      >
        {p === 'all' ? 'Todas' : TASK_PHASE_LABEL[p]}
        <span className="text-[11px] opacity-70">{counts[p]}</span>
      </button>
    ))}
  </div>
);

export const TaskRow: React.FC<{
  index: number;
  task: BataillonTask;
  onToggle: () => void;
  onRemove: () => void;
  /** Clicar no selo da fase avança para a próxima (antes → no retiro → pós-retiro). */
  onNextPhase: () => void;
}> = ({ index, task, onToggle, onRemove, onNextPhase }) => (
  <div className="flex items-center gap-3.5 border-b border-border px-[26px] py-[13px] transition-colors hover:bg-raised">
    <span className="font-display w-5 shrink-0 text-[11px] font-extrabold text-faint">
      {String(index + 1).padStart(2, '0')}
    </span>
    <SquareCheck
      checked={task.done}
      onChange={onToggle}
      label={`Concluir ${task.title}`}
    />
    <span
      className={cn(
        'flex-1 text-[14px]',
        task.done
          ? 'text-muted-foreground line-through decoration-faint'
          : 'text-foreground',
      )}
    >
      {task.title}
    </span>
    <button
      type="button"
      onClick={onNextPhase}
      title="Trocar quando acontece"
      className="col-label flex h-6 shrink-0 items-center rounded-full border border-border px-2.5 text-[9.5px] text-muted-foreground transition-colors hover:border-ring hover:text-white"
    >
      {TASK_PHASE_LABEL[task.phase]}
    </button>
    <RowIconButton icon="circle-x" label="Remover missão" onClick={onRemove} />
  </div>
);

/* ───────────── Efetivo (seção 04) ───────────── */

export const MemberRow: React.FC<{
  m: BataillonMember;
  captain?: 'Capitão Kids' | 'Capitão Jovens';
  onUnlink: () => void;
}> = ({ m, captain, onUnlink }) => (
  <div className="flex items-center gap-3 border-b border-border px-6 py-[11px] transition-colors hover:bg-raised">
    <SoldierAvatar name={m.name} photo={m.photo} />
    <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate text-[14px] font-semibold text-white">{m.name}</span>
        {captain && (
          <span className="col-label flex h-5 shrink-0 items-center rounded-full border border-sand/35 px-2 text-[9.5px] text-sand">
            {captain}
          </span>
        )}
      </span>
      <span className="text-[11.5px] text-faint">{memberMeta(m)}</span>
    </span>
    <RowIconButton icon="circle-x" label="Desvincular" onClick={onUnlink} />
  </div>
);

/** Cabeçalho de grupo do efetivo (Kids / Jovens) com a contagem. */
export const MemberGroupHeader: React.FC<{ label: string; count: number }> = ({
  label,
  count,
}) => (
  <div className="flex items-center gap-2 border-b border-border bg-sunken px-6 py-2">
    <span className="col-label text-[10px] text-sand">{label}</span>
    <span className="font-display text-[11px] font-extrabold text-faint">{count}</span>
  </div>
);

/** Dialog "Vincular recrutas" (print 05): 560px, raio 20, busca pílula, Sem batalhão | Todos, checkbox por linha. */
export const LinkRecruitsDialog: React.FC<{
  open: boolean;
  battalionName: string;
  candidates: BataillonMember[];
  counts: { free: number; all: number };
  scope: 'free' | 'all';
  onScope: (s: 'free' | 'all') => void;
  search: string;
  onSearch: (v: string) => void;
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
  saving?: boolean;
}> = ({
  open,
  battalionName,
  candidates,
  counts,
  scope,
  onScope,
  search,
  onSearch,
  onClose,
  onConfirm,
  saving,
}) => {
  const [sel, setSel] = React.useState<string[]>([]);
  React.useEffect(() => {
    if (open) setSel([]);
  }, [open]);
  React.useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && sel.length) onConfirm(sel);
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, sel, onClose, onConfirm]);
  if (!open) return null;
  const toggle = (id: string) =>
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center bg-[rgba(3,4,2,.72)] p-6 backdrop-blur-[3px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="anim-ci-in flex max-h-[86vh] w-full max-w-[560px] flex-col overflow-hidden rounded-[20px] border border-border bg-card shadow-[0_10px_30px_rgba(0,0,0,.7)]"
      >
        <div className="flex flex-col gap-3.5 border-b border-border px-6 pb-4 pt-[22px]">
          <span className="flex items-start gap-3">
            <span className="mr-auto flex flex-col gap-[5px]">
              <span className="font-display text-[19px] font-extrabold uppercase tracking-[-.02em] text-white">
                Vincular recrutas
              </span>
              <span className="text-[13px] text-muted-foreground">
                Ao {battalionName}. Recrutas de outro batalhão serão transferidos.
              </span>
            </span>
            <RowIconButton icon="x" label="Fechar" onClick={onClose} />
          </span>
          <label className="flex h-12 items-center gap-3 rounded-full border border-border bg-field px-4 focus-within:border-olive-line focus-within:bg-accent">
            <Icon name="search" className="size-[17px] text-olive-icon" />
            <input
              // biome-ignore lint/a11y/noAutofocus: popover/dialog recém-aberto, foco direto na busca (spec da tela)
              autoFocus
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Buscar recruta por nome ou cidade..."
              className="min-w-0 flex-1 bg-transparent text-[14.5px] text-white outline-none placeholder:text-faint"
            />
          </label>
          <span className="flex gap-2">
            {(['free', 'all'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onScope(s)}
                className={cn(
                  'flex h-[34px] items-center gap-2 rounded-full border px-3.5 text-[12.5px] font-bold',
                  scope === s
                    ? 'border-white bg-white text-black'
                    : 'border-border text-muted-foreground',
                )}
              >
                {s === 'free' ? 'Sem batalhão' : 'Todos'}
                <span className="font-display text-[11px] font-extrabold opacity-75">
                  {s === 'free' ? counts.free : counts.all}
                </span>
              </button>
            ))}
          </span>
        </div>
        <div className="flex-1 overflow-y-auto">
          {candidates.map((c) => {
            const on = sel.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                className={cn(
                  'flex w-full items-center gap-3 border-b border-border px-6 py-[11px] text-left hover:bg-raised',
                  on && 'bg-sand/[.04]',
                )}
              >
                <SquareCheck checked={on} decorative />
                <SoldierAvatar name={c.name} photo={c.photo} />
                <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                  <span className="text-[14px] font-semibold text-white">{c.name}</span>
                  <span className="text-[11.5px] text-faint">{memberMeta(c)}</span>
                </span>
                <span
                  className={cn(
                    'col-label flex h-6 items-center rounded-full border px-2.5 text-[10px]',
                    c.bataillonTag
                      ? 'border-border text-muted-foreground'
                      : 'border-sand/35 text-sand',
                  )}
                >
                  {c.bataillonTag ?? 'Livre'}
                </span>
              </button>
            );
          })}
          {!candidates.length && (
            <p className="px-6 py-10 text-center text-[13px] text-muted-foreground">
              Nenhum recruta encontrado.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2.5 border-t border-border px-6 py-3.5">
          <span className="mr-auto text-[12.5px] text-muted-foreground">
            {sel.length
              ? pl(sel.length, 'recruta selecionado', 'recrutas selecionados')
              : 'Nenhum selecionado'}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-full border border-border px-5 text-[13.5px] font-semibold text-muted-foreground hover:text-white"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!sel.length || saving}
            onClick={() => onConfirm(sel)}
            className="flex h-11 items-center gap-2 rounded-full bg-sand px-[22px] text-[13.5px] font-bold text-on-sand disabled:opacity-40"
          >
            <Icon name="user-plus" className="size-4" />
            Vincular
          </button>
        </div>
      </div>
    </div>
  );
};

/* ───────────── Dossiê (print 06) ───────────── */

/**
 * Prévia + impressão. Uma folha A4 por batalhão (`break-after: page`). Papel branco: aqui (e só aqui)
 * as cores são de impressão. O CSS de @media print está em styles/globals.css (montado por portal no <body>).
 */
export const DossierPreview: React.FC<{
  sheets: BataillonDetail[];
  onClose: () => void;
  issuedAt: string;
}> = ({ sheets, onClose, issuedAt }) =>
  createPortal(
    <div
      id="dossie-overlay"
      className="fixed inset-0 z-[60] overflow-y-auto bg-[rgba(3,4,2,.86)] px-6 pb-12 backdrop-blur-[4px]"
    >
      <div
        id="dossie-bar"
        className="sticky top-0 z-[2] -mx-6 mb-7 flex items-center gap-3 border-b border-border bg-background/90 px-7 py-3.5 backdrop-blur-xl"
      >
        <span className="mr-auto flex flex-col gap-1">
          <span className="font-display text-[17px] font-extrabold uppercase tracking-[-.02em] text-white">
            Dossiê dos batalhões
          </span>
          <span className="text-[12.5px] text-muted-foreground">
            {pl(sheets.length, 'batalhão', 'batalhões')} • uma folha A4 por batalhão
          </span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className="h-11 rounded-full border border-border px-5 text-[13.5px] font-semibold text-muted-foreground"
        >
          Fechar
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="flex h-11 items-center gap-2 rounded-full bg-sand px-[22px] text-[13.5px] font-bold text-on-sand"
        >
          <Icon name="printer" className="size-4" />
          Imprimir
        </button>
      </div>
      <div id="dossie" className="flex flex-col items-center gap-7">
        {sheets.map((b) => (
          <DossierSheet key={b.id} b={b} issuedAt={issuedAt} />
        ))}
      </div>
    </div>,
    document.body,
  );

const P = {
  ink: '#1B1D16',
  muted: '#5A5D4E',
  line: '#C9C9BD',
  soft: '#E2E1D6',
  head: '#F0EFE6',
  olive: '#4E7C3A',
};

const DossierSheet: React.FC<{ b: BataillonDetail; issuedAt: string }> = ({
  b,
  issuedAt,
}) => (
  <article
    className="ds-sheet flex min-h-[1123px] w-[794px] max-w-full flex-col gap-[22px] bg-white px-[60px] py-14 shadow-[0_20px_60px_rgba(0,0,0,.6)]"
    style={{ color: P.ink }}
  >
    <header
      className="flex items-center gap-3.5 border-b-2 pb-4"
      style={{ borderColor: P.ink }}
    >
      <span
        className="font-display flex size-[58px] items-center justify-center rounded-xl border-2 text-[16px] font-extrabold"
        style={{ borderColor: P.ink }}
      >
        {b.tag}
      </span>
      <span className="mr-auto flex flex-col gap-1">
        <span
          className="text-[9pt] font-bold uppercase tracking-[.16em]"
          style={{ color: P.muted }}
        >
          Aliste-se • Dossiê do batalhão
        </span>
        <span className="font-display text-[22pt] font-extrabold uppercase leading-none tracking-[-.02em]">
          {b.name}
        </span>
      </span>
      <span className="text-right text-[9pt]" style={{ color: P.muted }}>
        Emitido em
        <br />
        {issuedAt}
      </span>
    </header>
    <div className="grid grid-cols-4 rounded-lg border" style={{ borderColor: P.line }}>
      {[
        ['Major Kids', b.kidsMajor?.name],
        ['Capitão Kids', b.kidsCaptain?.name],
        ['Major Jovens', b.major?.name],
        ['Capitão Jovens', b.youthCaptain?.name],
      ].map(([l, v], i) => (
        <span
          key={l}
          className="flex min-w-0 flex-col gap-1 px-3 py-2.5"
          style={{ borderLeft: i ? `1px solid ${P.line}` : undefined }}
        >
          <span
            className="text-[8pt] font-bold uppercase tracking-[.14em]"
            style={{ color: P.muted }}
          >
            {l}
          </span>
          <span className="text-[10.5pt] font-bold leading-tight">{v ?? '—'}</span>
        </span>
      ))}
    </div>
    <section className="flex flex-col gap-1.5">
      <DossierLabel>Descrição</DossierLabel>
      {/* Já sanitizado no backend (allowlist do TipTap). */}
      <div
        className="ds-prose"
        dangerouslySetInnerHTML={{
          __html: b.description ?? '<p>Sem descrição cadastrada.</p>',
        }}
      />
    </section>
    <section className="flex flex-col gap-2">
      <DossierLabel>{`Missões (${b.tasks.length})`}</DossierLabel>
      {TASK_PHASE_ORDER.map((phase) => {
        const items = b.tasks.filter((t) => t.phase === phase);
        if (!items.length) return null;
        return (
          <div key={phase} className="flex flex-col gap-1.5">
            <span
              className="text-[8.5pt] font-bold uppercase tracking-[.14em]"
              style={{ color: P.muted }}
            >
              {TASK_PHASE_LABEL[phase]}
            </span>
            {items.map((t) => (
              <span
                key={t.id}
                className="flex items-start gap-2.5 text-[10.5pt] leading-[1.4]"
              >
                <span
                  className="mt-0.5 size-[13px] shrink-0 rounded-[3px] border-[1.5px]"
                  style={{ borderColor: P.ink }}
                />
                {t.title}
              </span>
            ))}
          </div>
        );
      })}
    </section>
    <section className="flex flex-col gap-2">
      <DossierLabel>{`Efetivo (${pl(b.soldiers.length, 'soldado', 'soldados')})`}</DossierLabel>
      {memberGroups(b.soldiers).map((g) => (
        <div key={g.key} className="flex flex-col gap-1.5">
          <span
            className="text-[8.5pt] font-bold uppercase tracking-[.14em]"
            style={{ color: P.muted }}
          >
            {`${g.label} (${g.items.length})`}
          </span>
          <div className="overflow-hidden rounded-lg border" style={{ borderColor: P.line }}>
            <div
              className="grid grid-cols-[40px_1fr_140px_60px_120px] px-3 py-[7px] text-[8pt] font-bold uppercase tracking-[.12em]"
              style={{ background: P.head, color: P.muted }}
            >
              <span>Nº</span>
              <span>Nome</span>
              <span>Cidade</span>
              <span>Idade</span>
              <span>Assinatura</span>
            </div>
            {g.items.map((m) => (
              <div
                key={m.id}
                className="grid grid-cols-[40px_1fr_140px_60px_120px] border-t px-3 py-[7px] text-[10pt]"
                style={{ borderColor: P.soft }}
              >
                <span style={{ color: P.muted }}>{m.soldierNumber ?? '—'}</span>
                <span className="font-semibold">{m.name}</span>
                <span>{m.city}</span>
                <span>{ageOf(m.birthday)}</span>
                <span className="border-b" style={{ borderColor: P.line }} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  </article>
);

const DossierLabel: React.FC<{ children: string }> = ({ children }) => (
  <span
    className="text-[9pt] font-bold uppercase tracking-[.16em]"
    style={{ color: P.olive }}
  >
    {children}
  </span>
);
